import { useCallback, useEffect, useRef, useState } from "react";
import { UseFormReturn } from "react-hook-form";
import {
  OnlineChatOrderPosItem,
  OnlineChatOrderPosPayload,
} from "src/pages/OrderManagement/OnlineChatOrder/api/interfaces";
import { countryCityDefaultValue } from "src/constant";
import { getPOSItemDetail } from "src/services/POS.service";
import { apiErrorHandler } from "src/utils/apiErrorHandler";
import { formatPhoneNumberForInputValue } from "src/utils/formatPhoneNumber";
import { PosCartSchemaType } from "../posCartSchema";
import { parseDeliveryDateTime } from "./chatOrderDelivery";
import { POSCartItemType, SelectedPackageProductType } from "./usePOSHandler";

interface UseChatOrderCartPrefillProps {
  chatOrderPrefill: OnlineChatOrderPosPayload | undefined;
  form: UseFormReturn<PosCartSchemaType, any, any>;
  setPOSCartItems: React.Dispatch<React.SetStateAction<POSCartItemType[]>>;
  setSelectedPackageProducts: React.Dispatch<
    React.SetStateAction<SelectedPackageProductType[]>
  >;
}

/**
 * POS validates prices as digits, so a numeric column that arrives as "0.00" fails —
 * silently, at the Next button, because a form-level rejection has nowhere to render.
 */
const toPriceString = (value: number | null | undefined) =>
  `${Math.max(0, Math.round(Number(value ?? 0)))}`;

/**
 * Turns a reviewed WhatsApp draft into a cart, once, when POS opens on it.
 *
 * Nothing here is a guess. By the time a draft reaches POS an admin has been through the
 * order screen and settled every decision that has money or stock behind it: which
 * product, which stem, which warehouse, how many, and what to charge. The draft screen
 * also refuses to hand over a line the shop cannot fill, so this builds a cart that is
 * expected to check out — rather than one that fails validation two screens later.
 *
 * What the admin keeps is every ordinary cart control: each line can still be edited,
 * re-priced or deleted, and POS re-checks availability at checkout regardless.
 */
const useChatOrderCartPrefill = ({
  chatOrderPrefill,
  form,
  setPOSCartItems,
  setSelectedPackageProducts,
}: UseChatOrderCartPrefillProps) => {
  const [isBuildingCart, setIsBuildingCart] = useState(false);

  /**
   * Guards against a second build. The effect depends on the payload, and rebuilding after
   * the admin has edited a line would silently undo their edit — including a line they
   * deliberately deleted, which would reappear.
   */
  const builtForChatOrderId = useRef<number | null>(null);

  const applyCustomer = useCallback(() => {
    if (!chatOrderPrefill) return;

    const { customer, new_customer_suggestion } = chatOrderPrefill;

    /**
     * The whole customer object, every time.
     *
     * POS validates this block as one unit, and react-hook-form's submit resolver rejects
     * silently: setting four of its ten fields left the Next button doing nothing at all,
     * and the only way forward was to re-pick the customer that was already on screen. The
     * shape below mirrors the cart's own customer picker exactly.
     */
    if (customer) {
      form.setValue("customer", {
        id: `${customer.id}`,
        name: customer.name,
        /** The picker's own formatter: the API sends E.164, the input renders groups. */
        phoneNumber: formatPhoneNumberForInputValue(customer.phone_number),
        birthdate: customer.birthdate,
        country: customer.country ?? "",
        city: customer.city ?? "",
        address: customer.address ?? undefined,
        type: "customer",
        customerTypeId: customer.customer_type_id
          ? `${customer.customer_type_id}`
          : "",
        customerType: customer.customer_type ?? "",
        hex: customer.hex ?? "",
        /** Assigning a salesperson is a decision nobody made in a chat. */
        isAssignSalesperson: false,
        salespersonId: undefined,
        contractDatePeriod: null,
      });

      return;
    }

    if (!new_customer_suggestion) return;

    /**
     * A buyer the CRM has never seen, in exactly the shape the Create Customer modal
     * produces: an empty-string id, which is what tells checkout to create them.
     *
     * Country and city come from the same shared default that modal uses. They are a
     * client-side requirement only — checkout sends neither, so nothing invented here
     * reaches the CRM — but the cart renders no field for them, so an earlier version
     * that left them blank produced a Next button which could never be satisfied and an
     * admin forced to abandon the draft's buyer and pick an existing customer instead.
     */
    form.setValue("customer", {
      id: "",
      name: new_customer_suggestion.name ?? "",
      phoneNumber: formatPhoneNumberForInputValue(
        new_customer_suggestion.phone_number
      ),
      birthdate: null,
      country: countryCityDefaultValue.country,
      city: countryCityDefaultValue.city,
      /**
       * Left blank deliberately: the address in a florist conversation is where the
       * flowers go, not where the buyer lives, and copying it here would write the wrong
       * address into the CRM permanently.
       */
      address: undefined,
      type: "customer",
      customerTypeId: "",
      customerType: "",
      hex: "",
      isAssignSalesperson: false,
      salespersonId: undefined,
      contractDatePeriod: null,
    });
  }, [chatOrderPrefill, form]);

  /** One draft line as the cart line POS would have built through its own modal. */
  const toOrder = useCallback(
    (item: OnlineChatOrderPosItem): PosCartSchemaType["orders"][number] => {
      const base = {
        id: item.variant_id,
        note: "",
        markAsRent: false,
        price: toPriceString(item.sell_price),
        discount: toPriceString(item.discount),
        quantity: `${item.quantity}`,
      };

      /**
       * A loose product is priced and stocked per warehouse, so its quantity lives on the
       * warehouse row; a package is priced whole and carries its contents as products.
       * Same two shapes the add-product modal produces for the same two cases.
       */
      if (item.item_type === "product") {
        return {
          ...base,
          type: "product",
          markAsRent: item.mark_as_rent,
          products: [],
          warehouses: undefined,
          warehouse: {
            id: item.warehouse_id ?? 0,
            name: item.warehouse_name ?? "",
            quantity: `${item.quantity}`,
          },
        } as PosCartSchemaType["orders"][number];
      }

      return {
        ...base,
        type: "package",
        products: item.products.map((product) => ({
          id: `${product.product_variant_id}`,
          warehouseId: `${product.warehouse_id ?? 0}`,
          warehouseName: product.warehouse_name ?? "",
          quantity: `${product.quantity}`,
          /** Every row's own decision, exactly as the admin left it on the order screen. */
          price: toPriceString(product.sell_price),
          discount: toPriceString(product.discount),
          markAsRent: product.mark_as_rent,
        })),
        warehouses: undefined,
        warehouse: { id: 0, name: "", quantity: `${item.quantity}` },
      } as PosCartSchemaType["orders"][number];
    },
    []
  );

  const buildCart = useCallback(async () => {
    if (!chatOrderPrefill) return;
    if (builtForChatOrderId.current === chatOrderPrefill.chat_order_id) return;

    builtForChatOrderId.current = chatOrderPrefill.chat_order_id;

    applyCustomer();

    /**
     * The order note carries what the conversation settled that no field can hold.
     *
     * The recipient's name, phone and address are NOT copied in here — they have real
     * fields on the delivery step and are prefilled there, and duplicating them as prose
     * would leave two versions of an address free to disagree. What does belong here is a
     * delivery time that is not a clock reading: "pagi" cannot become a timestamp without
     * inventing a promise, so it stays as words a florist reads.
     */
    const { unparsedTime } = parseDeliveryDateTime(
      chatOrderPrefill.recipient.delivery_date,
      chatOrderPrefill.recipient.delivery_time
    );

    const note = [
      chatOrderPrefill.notes,
      unparsedTime ? `Waktu kirim: ${unparsedTime}` : null,
    ]
      .filter(Boolean)
      .join("\n");

    if (note) form.setValue("note", note);

    /**
     * The one required field on the cart that no conversation will ever mention. Filled
     * from the deployment's own default so the admin is not picking the same value every
     * time; the dropdown still works if this shop runs more than one operation.
     */
    if (chatOrderPrefill.business_operation) {
      form.setValue(
        "businessOperationId",
        chatOrderPrefill.business_operation.id
      );
      form.setValue(
        "businessOperationName",
        chatOrderPrefill.business_operation.name
      );
    }

    if (!chatOrderPrefill.items.length) return;

    setIsBuildingCart(true);

    try {
      /**
       * Display metadata — image, sub-category colour, stock unit — comes from the POS's
       * own item endpoint rather than the draft payload, so a cart line built from a chat
       * is indistinguishable from one built by hand. A handful of lines per draft, so the
       * round trips are cheap and run together.
       */
      const details = await Promise.all(
        chatOrderPrefill.items.map((item) =>
          getPOSItemDetail({
            id: item.variant_id,
            type: item.item_type,
            edit: 0,
            transactionId: undefined,
          })
        )
      );

      const cartItems: POSCartItemType[] = [];
      const packageProducts: SelectedPackageProductType[] = [];

      chatOrderPrefill.items.forEach((item, index) => {
        const detail = details[index]?.data;

        if (detail) {
          cartItems.push({
            id: detail.id,
            name: detail.name,
            type: detail.type,
            sub_category: { hex: detail.sub_category.hex },
            image_url: detail.image_url,
            product: detail.product,
            package: detail.package,
            stocks:
              detail.stocks?.map((stock) => ({
                warehouse: {
                  id: stock.warehouse.id,
                  name: stock.warehouse.name,
                },
              })) ?? [],
            available: detail.available,
            is_rent: detail.is_rent,
            is_purchase: detail.is_purchase,
            item_stock_unit: { name: detail.item_stock_unit.name },
            products: item.products.map((product) => ({
              id: `${product.product_variant_id}` as any,
              warehouseName: product.warehouse_name ?? "",
            })),
          } as POSCartItemType);
        }

        /**
         * Every product row a package line contains, including the flowers the package
         * itself does not list. A board flower is defined in the catalogue as a greeting
         * card and nothing else, so without these the cart would show a card where the
         * order says lavender and lily. A loose product has no such rows.
         */
        if (item.item_type !== "package") return;

        item.products.forEach((product) => {
          packageProducts.push({
            id: product.product_variant_id,
            packageVariantId: item.variant_id,
            productName: product.variant?.product?.name ?? "",
            variantName: product.variant?.name ?? product.label,
            itemStockUnit: product.variant?.product?.item_stock_unit?.name ?? "",
            stocks: product.variant?.stocks ?? [],
            quantity: product.quantity,
            isRent: Boolean(product.variant?.is_rent),
            isPurchase: Boolean(product.variant?.is_purchase),
            rentPrice: product.variant?.rent_price ?? 0,
            sellPrice: product.sell_price,
          });
        });
      });

      setPOSCartItems(cartItems);
      setSelectedPackageProducts(packageProducts);

      form.setValue("orders", chatOrderPrefill.items.map(toOrder), {
        shouldValidate: true,
      });
    } catch (error) {
      /**
       * The cart is left empty rather than half-built. A partial cart looks complete and
       * would be checked out one item short; an empty one sends the admin to the product
       * grid, which is somewhere they know how to work.
       */
      apiErrorHandler({ error });
    } finally {
      setIsBuildingCart(false);
    }
  }, [
    chatOrderPrefill,
    applyCustomer,
    form,
    setPOSCartItems,
    setSelectedPackageProducts,
    toOrder,
  ]);

  useEffect(() => {
    buildCart();
  }, [buildCart]);

  return { isBuildingCart };
};

export default useChatOrderCartPrefill;
